import { useCallback, useRef, useState } from 'react';
import { streamAiMessage, type SendAiMessageInput } from '../api/messages';

/** @deprecated Legacy sub-project #1 shape — kept for backwards compat; always [] in Phase 4a. */
export interface DraftRef {
  pluginId: string;
  entityType: string;
  entityId: string;
}

export interface UndoBannerEntry {
  draftId: string;
  bundleId: string;
  deadlineAt: Date;
  label: string;
}

export interface ChatStreamState {
  isStreaming: boolean;
  currentText: string;
  toolEvents: Array<{ id: string; name: string; uiLabel?: string; done: boolean }>;
  /** @deprecated Always [] in Phase 4a — use bundleIds instead. */
  draftRefs: DraftRef[];
  /** BundleIds produced in the current turn (Phase 4a). */
  bundleIds: string[];
  /** Undo banners for auto-applied drafts (Phase 4a). */
  undoBanners: UndoBannerEntry[];
  error: string | null;
}

export interface ChatStreamFinal {
  finalText: string;
  finalTraces: Array<{ id: string; name: string; uiLabel?: string; done: boolean }>;
  /** @deprecated Always [] in Phase 4a. */
  finalDraftRefs: DraftRef[];
  persisted: { userMessageId: string; assistantMessageId: string } | null;
  error: string | null;
}

const initial: ChatStreamState = {
  isStreaming: false,
  currentText: '',
  toolEvents: [],
  draftRefs: [],
  bundleIds: [],
  undoBanners: [],
  error: null,
};

export const useChatStream = () => {
  const [state, setState] = useState<ChatStreamState>(initial);
  const abortRef = useRef<AbortController | null>(null);

  // `send` returns the terminal state directly — no need for callers to read
  // hook state after await (which is racy across render boundaries). State
  // still updates during streaming so a streaming line can render live.
  const send = useCallback(async (input: SendAiMessageInput): Promise<ChatStreamFinal> => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setState({ ...initial, isStreaming: true });

    // Local accumulators — authoritative for the return value. Hook state
    // mirrors them for UI rendering during the stream.
    let text = '';
    const traces: Array<{ id: string; name: string; uiLabel?: string; done: boolean }> = [];
    let persisted: { userMessageId: string; assistantMessageId: string } | null = null;
    let error: string | null = null;

    try {
      for await (const ev of streamAiMessage(input, ac.signal)) {
        if (ev.type === 'token') {
          text += ev.data.text;
          setState((s) => ({ ...s, currentText: text }));
        } else if (ev.type === 'tool_use_started') {
          traces.push({ id: ev.data.id, name: ev.data.name, uiLabel: ev.data.uiLabel, done: false });
          setState((s) => ({ ...s, toolEvents: [...traces] }));
        } else if (ev.type === 'tool_use_result') {
          const traceId = (ev.data as { trace?: { id?: string } }).trace?.id;
          if (traceId) {
            const idx = traces.findIndex((t) => t.id === traceId);
            if (idx >= 0) traces[idx] = { ...traces[idx]!, done: true };
            setState((s) => ({ ...s, toolEvents: [...traces] }));
          }
        } else if (ev.type === 'draft_created') {
          // Phase 4a: track the bundleId so the message render can show <DraftBundleCard>
          const { bundleId } = ev.data;
          setState((s) => ({
            ...s,
            bundleIds: s.bundleIds.includes(bundleId) ? s.bundleIds : [...s.bundleIds, bundleId],
          }));
        } else if (ev.type === 'draft_auto_applied') {
          // Spawn an undo banner above chat input
          const { draftId, bundleId, undoDeadlineAt } = ev.data;
          setState((s) => ({
            ...s,
            undoBanners: [
              ...s.undoBanners,
              { draftId, bundleId, deadlineAt: new Date(undoDeadlineAt), label: 'Applied a change' },
            ],
          }));
        } else if (ev.type === 'bundle_ready') {
          // Bundle finalized; ensure it's in the list
          const { bundleId } = ev.data;
          setState((s) => ({
            ...s,
            bundleIds: s.bundleIds.includes(bundleId) ? s.bundleIds : [...s.bundleIds, bundleId],
          }));
        } else if (ev.type === 'bundle_available') {
          // Part B: a reviewable bundle exists even though the happy-path
          // draft_created/bundle_ready events didn't fire (dedup re-run, Phase C
          // failure, or a create that couldn't be staged). Always surface the
          // card; on create_failed also raise the actionable message.
          const { bundleId, reason, message } = ev.data;
          if (reason === 'create_failed') {
            error = message ?? error;
          }
          setState((s) => ({
            ...s,
            bundleIds:
              bundleId && !s.bundleIds.includes(bundleId)
                ? [...s.bundleIds, bundleId]
                : s.bundleIds,
            error: reason === 'create_failed' ? (message ?? s.error) : s.error,
          }));
        } else if (ev.type === 'bundle_rejected_conflict') {
          // Surface a toast / log; for now, silently drop
        } else if (ev.type === 'writes_disabled') {
          // Surface a toast / banner; for now, silently drop
        } else if (ev.type === 'error') {
          error = ev.data.message;
          setState((s) => ({ ...s, error }));
        } else if (ev.type === 'message_persisted') {
          persisted = ev.data;
        } else if (ev.type === 'done') {
          break;
        }
      }
    } catch (err) {
      if ((err as { name?: string }).name !== 'AbortError') {
        error = err instanceof Error ? err.message : 'Stream failed';
        setState((s) => ({ ...s, error }));
      }
    } finally {
      setState((s) => ({ ...s, isStreaming: false }));
    }

    return { finalText: text, finalTraces: traces, finalDraftRefs: [], persisted, error };
  }, []);

  const cancel = useCallback(() => abortRef.current?.abort(), []);
  const reset = useCallback(() => setState(initial), []);

  const dismissUndoBanner = useCallback((draftId: string) => {
    setState((s) => ({
      ...s,
      undoBanners: s.undoBanners.filter((b) => b.draftId !== draftId),
    }));
  }, []);

  return { ...state, send, cancel, reset, dismissUndoBanner };
};
